import { Avatar, Box, Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CardDataNotFound from "../../components/CardDataNotFound/CardDataNotFound";
import CardTitleWithSubtitle from "../../components/CardTitleWithSubtitle/CardTitleWithSubtitle";
import CustomCard from "../../components/CustomCard/CustomCard";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import TablePaginated from "../../components/TablePaginated/TablePaginated";
import { useIocContext } from "../../contexts/ioc/IocContext";
import { Types } from "../../ioc/types";
import { GetAllFinancialQuery } from "../../modules/financial/models/GetAllFinancialQuery";
import { IFinancialService } from "../../modules/financial/models/IFinancialService";
import { formatCurrency } from "../../utils/currencyFormatter";
import { ISODateToFormat } from "../../utils/dateFormatter";
import { downloadExcel } from "../../utils/download";
import { TableHeaderColumns } from "./models/TableHeaderColumns";
import useDialogAlert from "../../hooks/DialogAlert";
import { StatusInvoiceToString, calcPagesQuantity } from "../../utils";
import { Content } from "../../modules/financial/models/GetAllFinancialDTO";
import StatementFilter from "./Sections/StatementFilter";
import ActionButtonsTable from "./Sections/ActionButtonsTable";
import { StatementRow } from "./models/StatementRows";

const ROW_PER_PAGE = 10;

const ConsultStatementPage: React.FC = () => {
  const [data, setData] = useState<StatementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [errorData, setErrorData] = useState(false);

  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIocContext();
  const financialService = serviceContainer.get<IFinancialService>(
    Types.Financial.IFinancialService
  );

  const [filter, setFilter] = useState<GetAllFinancialQuery | null>(null);

  const getAllFinancial = useCallback(async () => {
    if (!filter) return;
    try {
      setIsLoadingData(true);
      const response = await financialService.getAllFinancial(filter);

      const newData = response.content.map((item: Content, index) => ({
        id: `${index}${item.customerNumber}`,
        document: item.documentNumber,
        dateOfIssue: ISODateToFormat(item.documentDate),
        dueDate: ISODateToFormat(item.dueDate),
        financialStatus: StatusInvoiceToString(item.statusInvoice),
        branch: item.divisionDescription ? item.divisionDescription : "-",
        value: formatCurrency(item.value),
        accessKey: item.referenceKey,
        companyBank: item.bankName,
        paymentMethod: item.paymentMethod,
        paymentType: item.paymentType,
        ourNumber: item.reference3,
        compensationDate: ISODateToFormat(item.quittanceDate),
        description: item.documentItemDescription,
        atemId: item.companyCode,
        invoiceId: item.documentNumber,
        installmentNumber: item.documentItem,
        year: item.fiscalYear,
        categoryTranslation: item.categoryTranslation,
        category: item.category,
        CNPJ: item.CNPJ ? item.CNPJ : "-",
        fiscalYear: item.fiscalYear,
        companyCode: item.companyCode,
        documentItem: item.documentItem,
        customerName: item.customerName,
        customerNumber: item.customerNumber,
      }));

      setData(newData);
      setTotal(response.total);
      setErrorData(newData.length === 0);
    } catch (error) {
      snackbar({ message: "Erro ao carregar extrato.", variant: "error" });
      setErrorData(true);
    } finally {
      setIsLoadingData(false);
    }
  }, [filter, financialService, snackbar]);

  const downloadReport = useCallback(async () => {
    if (!filter) return;
    try {
      setIsLoadingDownload(true);
      const response = await financialService.downloadReport(filter);
      downloadExcel(response);
      snackbar({
        message: "Download realizado com sucesso",
        variant: "success",
      });
    } catch (error) {
      snackbar({ message: "Erro ao fazer download.", variant: "error" });
    } finally {
      setIsLoadingDownload(false);
    }
  }, [filter, financialService, snackbar]);

  useEffect(() => {
    if (filter) {
      getAllFinancial();
    } else {
      setData([]);
      setTotal(0);
      setErrorData(false);
    }
  }, [filter]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{ width: 60, height: 60 }}
          />
        ),
      }}
    >
      <Box
        sx={{
          height: "130vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <QueryPageTitleCard title="Consultar Extrato" />
            </Grid>

            <Grid item xs={12}>
              <CustomCard
                sx={{ border: "1px solid #DFE0EB", boxShadow: "none" }}
                padding={2.4}
              >
                <CardTitleWithSubtitle
                  title="Consultar Extrato"
                  subtitle="Informe os filtros abaixo para consultar o extrato"
                />

                <StatementFilter
                  onFilterChange={setFilter}
                  onDownloadReport={downloadReport}
                  isLoadingDownload={isLoadingDownload}
                  errorData={errorData}
                />
              </CustomCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              height: "100%",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!filter ? (
              <CardDataNotFound
                icon="search-empty"
                title="Selecione o CNPJ para começar"
                subtitle="Informe outros critérios de busca e comece a consultar!"
              />
            ) : errorData ? (
              <CardDataNotFound
                icon="data-not-found"
                title="Não foi encontrado resultado"
                subtitle="Informe outros critérios de busca e consulte novamente"
              />
            ) : (
              <TablePaginated
                columns={TableHeaderColumns}
                data={data.map((item, index) => ({
                  ...item,
                  icon: (
                    <ActionButtonsTable
                      index={index}
                      row={item}
                      financialService={financialService}
                    />
                  ),
                }))}
                rowsPerPage={ROW_PER_PAGE}
                isLoadingData={isLoadingData}
                loadFromServer
                onChangePage={(newPage) =>
                  setFilter((prev) =>
                    prev ? { ...prev, page: newPage } : null
                  )
                }
                paginationProps={{
                  pagesTotal: calcPagesQuantity(ROW_PER_PAGE, total),
                  page: filter?.page || 1,
                }}
                resultsTotal={total}
                isStickyHeader
              />
            )}
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default ConsultStatementPage;
